import { useCallback, useState } from 'react';
import { addWireDays, dealDemandSchema, formatHours, type DealDemand } from '@forecastify/shared';
import { Empty, ListToolbar, Skeleton, Tooltip, cn } from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import { useLiveInvalidation } from '../../live/use-live';
import { DEAL_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { useFirstLoad } from '../use-first-load';
import { shortDay } from './board-format';
import { dealModeToolbar } from './board-toolbar';
import type { DealModeProps } from './deals-modes';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D7 · *Dealek — Erőforrás-igény* · `phase6/04`.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Role × week: what the open deals' placeholders ask for, over what the role's people have free
 * that week. Red where the ask is larger. Both figures are the server's (`deal-insights.service.ts`
 * `demand`) and the screen only formats them.
 *
 * ⚠️ **Raw hours only.** The mockup's switch weights the demand by each deal's probability; the
 * server sends the raw figure alone, so the switch would need a second figure on the wire — the
 * label states which one this is instead of offering a choice that does nothing.
 */

type T = ReturnType<typeof useT>;

/** Below this the mockup draws a dash: a few minutes of demand is rounding, not a need. */
const NEGLIGIBLE = 0.04;

export function DealDemandScreen(props: DealModeProps) {
  const t = useT();
  const { locale } = useI18n();
  const session = useSession();
  const [demand, setDemand] = useState<DealDemand | null>(null);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [search, setSearch] = useState('');

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    const response = await session.request('/deals/demand').catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    const parsed = dealDemandSchema.safeParse(await response.json());
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setDemand(parsed.data);
  }, [session]);

  const loading = useFirstLoad(reload);
  useLiveInvalidation(DEAL_TOPICS, reload);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* ⚠️ The search narrows the roles by name — the grid is the server's whole aggregate, a
          handful of rows, and the endpoint takes no filter. */}
      <ListToolbar {...dealModeToolbar(t, props)} search={search} onSearchChange={setSearch} />
      <ScreenState failure={failure} onRetry={() => void reload()}>
        {loading || demand === null ? (
          <DemandSkeleton />
        ) : demand.roles.length === 0 || demand.weeks.length === 0 ? (
          <Empty
            icon="users"
            title={t('dealDemand.empty.title')}
            body={t('dealDemand.empty.body')}
          />
        ) : (
          <DemandGrid
            t={t}
            locale={locale}
            demand={demand}
            needle={search.trim().toLocaleLowerCase(locale)}
          />
        )}
      </ScreenState>
    </div>
  );
}

function DemandGrid({
  t,
  locale,
  demand,
  needle,
}: {
  readonly t: T;
  readonly locale: string;
  readonly demand: DealDemand;
  readonly needle: string;
}) {
  const first = demand.weeks[0] ?? '';
  const lastMonday = demand.weeks.at(-1) ?? first;
  const last = addWireDays(lastMonday, 6) ?? lastMonday;
  const roles = demand.roles.filter(
    (role) => needle === '' || role.name.toLocaleLowerCase(locale).includes(needle),
  );

  return (
    <div className="min-h-0 flex-1 overflow-auto p-5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="bg-muted rounded-md px-2 py-0.5 text-[12px] font-medium">
          {t('dealDemand.raw')}
        </span>
        <span className="text-muted-foreground text-[11.5px]">
          {t('dealDemand.note', { from: shortDay(first, locale), to: shortDay(last, locale) })}
        </span>
      </div>
      <div className="bg-card mt-3 overflow-x-auto rounded-xl border">
        <table className="w-full text-[12.5px]">
          <caption className="sr-only">{t('dealDemand.table')}</caption>
          <thead>
            <tr className="text-muted-foreground bg-muted/40 border-b text-[10.5px] font-medium tracking-wide uppercase">
              <th
                scope="col"
                className="bg-muted sticky left-0 z-10 w-[180px] px-3 py-2 text-left font-medium"
              >
                {t('dealDemand.column.role')}
              </th>
              {demand.weeks.map((week) => (
                <th
                  key={week}
                  scope="col"
                  className="px-2 py-2 text-right font-medium whitespace-nowrap"
                >
                  {shortDay(week, locale)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr key={role.projectRoleId} className="hover:bg-muted/20 border-b transition-colors">
                <th
                  scope="row"
                  className="bg-card sticky left-0 z-10 px-3 py-2 text-left font-normal"
                >
                  <span className="flex items-center gap-2">
                    <span
                      aria-hidden
                      className="size-2 shrink-0 rounded-[3px]"
                      style={{ background: role.color }}
                    />
                    <span className="font-medium whitespace-nowrap">{role.name}</span>
                  </span>
                </th>
                {demand.weeks.map((week, index) => {
                  const asked = Number(role.demand[index] ?? 0);
                  const free = Number(role.capacity[index] ?? 0);
                  const some = asked > NEGLIGIBLE;
                  const short = some && asked > free;
                  return (
                    <Tooltip
                      key={week}
                      asChild
                      content={
                        !some
                          ? t('dealDemand.cellNone')
                          : short
                            ? t('dealDemand.cellShort', {
                                demand: formatHours(asked),
                                free: formatHours(free),
                                gap: formatHours(asked - free),
                              })
                            : t('dealDemand.cell', {
                                demand: formatHours(asked),
                                free: formatHours(free),
                              })
                      }
                    >
                      <td className="px-2 py-1.5 text-right">
                        <span
                          className={cn(
                            'block text-[12px] tabular-nums',
                            !some
                              ? 'text-muted-foreground'
                              : short
                                ? 'text-destructive font-medium'
                                : 'text-foreground',
                          )}
                        >
                          {some ? formatHours(asked) : '–'}
                        </span>
                        <span className="text-muted-foreground/70 block text-[10.5px] tabular-nums">
                          {formatHours(free)}
                        </span>
                      </td>
                    </Tooltip>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-muted-foreground mt-2 text-[11.5px] leading-relaxed">
        {t('dealDemand.legend')}
      </p>
    </div>
  );
}

function DemandSkeleton() {
  const t = useT();
  return (
    <div className="min-h-0 flex-1 overflow-auto p-5">
      <div role="status" aria-live="polite" className="sr-only">
        {t('state.loading')}
      </div>
      <div aria-hidden className="bg-card mt-3 flex flex-col gap-4 rounded-xl border p-4">
        {[0, 1, 2, 3, 4, 5, 6].map((index) => (
          <Skeleton key={index} width={index % 2 === 0 ? '92%' : '80%'} />
        ))}
      </div>
    </div>
  );
}
